// web_components/wcAgeGateModal/wcAgeGateModal.ts

import { WebComponent_Base } from '../../WebComponent_Base';
import { renderProductHTML } from './wcAgeGateModal_Renderer';
import { addEventListener, removeEventListener } from '../../../utils/events';
import { applyFocusTrap } from '../../../utils/focusTrap';
import { WebComponent_Utils } from '../../WebComponent_Utils';
import { removeSkeletonLoading } from '../../../utils/SkeletonLoading';
import { setCookie } from '../../../utils/cookies';

/**
 * A custom web component for managing cookie consent settings.
 * Provides three toggle options: Essential (always on), Functional, and Marketing.
 */
export class WcAgeGateModal extends WebComponent_Base {
	public instanceId: string;
	private lastFocusedElement: Element | null = null;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'wcAgeGateModal';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Perform additional setup after initialization.
	 */
	protected afterInitialize() {
		removeSkeletonLoading(this);
		this.manageFocus();
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Sets up event listeners for the modal.
	 * This includes handling the 'save preferences' and 'close modal' actions.
	 */
	protected attachListeners(): void {
		const continueBtn = this.shadowRoot!.getElementById('wc-age-gate-modal__continue-btn');
		if (!continueBtn) return;
		addEventListener(continueBtn, 'click', this.closeModal.bind(this));
	}

	/**
	 * Closes the modal and restores focus to the last focused element.
	 * Ensures logical focus flow after the modal is dismissed.
	 * Removes the keydown listener to avoid memory leaks.
	 * Calls the function to remove the body 'modal-active' class.
	 * Logs a cookie to confirm the user has declared that they are 18+.
	 */
	closeModal() {
		this.remove();
		document.querySelector('body')?.classList.remove('modal-active');

		if (this.lastFocusedElement) {
			(this.lastFocusedElement as HTMLElement).focus();
		}

		// Remove the keydown event listener when the modal is closed
		removeEventListener(document, 'keydown', this.handleKeydown);
		setCookie('over18Confirmed', 'true', 365);

		this.dispatchEvent(new CustomEvent('age-gate-approved', { bubbles: true }));
	}

	/**
	 * Manages focus within the modal to ensure accessibility.
	 * Uses `requestAnimationFrame` to delay focus setting until after rendering is complete.
	 * Applies focus only if the Consent Modal isn't in the document.
	 */
	manageFocus() {
		this.lastFocusedElement = document.activeElement;
		const consentModal = document.querySelector('wc-consent-modal') as HTMLElement;
		const consentManageModal = document.querySelector('wc-consent-manage-modal') as HTMLElement;
		if (consentModal || consentManageModal) return;

		requestAnimationFrame(() => {
			const modal = this.shadowRoot!.querySelector('.wc-age-gate-modal') as HTMLElement;
			if (modal) {
				modal.focus();
				applyFocusTrap(modal, true);
			}
		});
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-age-gate-modal', WcAgeGateModal);
